import { useState } from 'react'
import styled, { css } from 'styled-components'
import {
  Select,
  MenuItem,
  Typography,
  TextField,
  RadioGroup,
  FormControlLabel,
  Radio,
  FormLabel,
  FormControl,
  Tooltip,
} from '@mui/material'
import { useTranslation } from "react-i18next"

const sectionForOrder = order => {
  if (order < 10) return 'login'
  if (order < 20) return 'usual_log'
  if (order < 40) return 'role'

  return 'personal'
}

const Row = styled.div.attrs(({ $order }) => ({
  'data-section': sectionForOrder($order),
  'data-last': [2, 13, 33, 47].includes($order),
}))`
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 550px;
  order: ${({ $order }) => $order};
  padding: 8px 16px;
  background: #f8fafc;
  border-left: 1px solid #d6d7db;
  border-right: 1px solid #d6d7db;

  &[data-last='true'] {
    padding-bottom: 16px;
    border-bottom: 1px solid #d6d7db;
    border-radius: 0 0 8px 8px;
  }
`

const FormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;

  > [data-section] {
    display: none;
  }

  ${({ $expandedSection }) => css`
    > [data-section='${$expandedSection}'] {
      display: flex;
    }
  `}
`

const SectionHeading = styled.button`
  order: ${({ $order }) => $order};
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  margin: 12px 0 0;
  padding: 14px 16px;
  border: 1px solid #d6d7db;
  border-radius: ${({ $expanded }) => ($expanded ? '8px 8px 0 0' : '8px')};
  background: ${({ $expanded }) => ($expanded ? '#eef4ff' : '#fff')};
  color: inherit;
  cursor: pointer;
  text-align: left;

  h3 {
    margin: 0;
  }
`

const BRASILIA_TIMEZONE = { name: 'Brasilia' }

const sortTimezoneOptions = timezoneOptions => {
  const timezonesExceptBrasilia = timezoneOptions
    .filter(option => option.name !== BRASILIA_TIMEZONE.name)
    .sort((first, second) => first.name.localeCompare(second.name))

  return [BRASILIA_TIMEZONE, ...timezonesExceptBrasilia]
}

const UserForm = ({
  shiftOptions,
  userOptions,
  userData,
  setUserData,
  timezoneOptions,
  caffeineOptions,
}) => {
  const { t } = useTranslation('common')
  const sortedTimezoneOptions = sortTimezoneOptions(timezoneOptions)
  const [expandedSection, setExpandedSection] = useState('login')

  const accordionHeader = (section, order) => (
    <SectionHeading
      type="button"
      $order={order}
      $expanded={expandedSection === section}
      aria-expanded={expandedSection === section}
      onClick={() =>
        setExpandedSection(currentSection =>
          currentSection === section ? null : section,
        )
      }
    >
      <Typography variant="h6" component="h3">
        {t(`user_form.categories.${section}`)}
      </Typography>
      <Typography aria-hidden>{expandedSection === section ? '−' : '+'}</Typography>
    </SectionHeading>
  )

  return (
    <FormContainer $expandedSection={expandedSection}>
      {accordionHeader('login', 0)}
      {accordionHeader('usual_log', 10)}
      {accordionHeader('role', 20)}
      {accordionHeader('personal', 40)}
      <Row $order={41}>
        <Typography>{t('user_form.name')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.placeholder.name')}
          value={userData.name}
          onChange={(e) => setUserData({ ...userData, name: e.target.value })}
        />
      </Row>
      <Row $order={24}>
        <Typography>{t('user_form.company_designated_id')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.company_designated_id')}
          value={userData.company_designated_id}
          onChange={(e) => setUserData({ ...userData, company_designated_id: e.target.value })}
        />
      </Row>
      <Row $order={1}>
        <Typography>{t('user_form.email')}</Typography>
        <TextField
          type="email"
          fullWidth
          placeholder={t('user_form.email')}
          value={userData.email}
          onChange={(e) => setUserData({ ...userData, email: e.target.value })}
        />
      </Row>
      <Row $order={42}>
        <Typography>{t('user_form.mobile')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.mobile')}
          type="tel"
          value={userData.mobile}
          onChange={(e) => setUserData({ ...userData, mobile: e.target.value })}
        />
      </Row>
      <Row $order={43}>
        <Typography>{t('user_form.legal_id')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.legal_id')}
          value={userData.legal_id}
          onChange={(e) => setUserData({ ...userData, legal_id: e.target.value })}
        />
      </Row>
      <Row $order={44}>
        <Typography>{t('user_form.gender')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.gender')}
          type="tel"
          value={userData.gender}
          onChange={(e) => setUserData({ ...userData, gender: e.target.value })}
        />
      </Row>
      <Row $order={45}>
        <Typography>{t('user_form.birthday')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.birthday')}
          type="date"
          value={userData.birthday}
          onChange={(e) => setUserData({ ...userData, birthday: e.target.value })}
        />
      </Row>
      <Row $order={46}>
        <Typography>{t('user_form.locale')}</Typography>
        <Select
          fullWidth
          value={userData.locale}
          onChange={(e) => setUserData({ ...userData, locale: e.target.value })}
          displayEmpty
        >
          <MenuItem value="" disabled>{t('user_form.placeholder.locale')}</MenuItem>
          <MenuItem value="pt-BR">pt-BR</MenuItem>
          <MenuItem value="en-US">en-US</MenuItem>
          <MenuItem value="es-ES">es-ES</MenuItem>
          <MenuItem value="fr-FR">fr-FR</MenuItem>
        </Select>
      </Row>
      <Row $order={47}>
        <Typography>{t('user_form.timezone')}</Typography>
        <Select
          fullWidth
          value={userData.timezone}
          onChange={(e) => setUserData({ ...userData, timezone: e.target.value })}
          displayEmpty
        >
          <MenuItem value="" disabled>{t('user_form.placeholder.timezone')}</MenuItem>
          {sortedTimezoneOptions.map(option => (
            <MenuItem key={option.name} value={option.name}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </Row>
      <Row $order={22}>
        <Typography>{t('user_form.role_name')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.role_name')}
          value={userData.role_name}
          onChange={(e) => setUserData({ ...userData, role_name: e.target.value })}
        />
      </Row>
      <Row $order={23}>
        <Typography>{t('user_form.role_risk_level')}</Typography>
        <Select
          fullWidth
          value={userData.role_risk_level}
          onChange={(e) => setUserData({ ...userData, role_risk_level: e.target.value })}
          displayEmpty
        >
          <MenuItem value="" disabled>{t('user_form.role_risk_level')}</MenuItem>
          <MenuItem value="high">{t('high')}</MenuItem>
          <MenuItem value="medium">{t('medium')}</MenuItem>
          <MenuItem value="low">{t('low')}</MenuItem>
        </Select>
      </Row>
      <Row $order={25}>
        <Typography>{t('user_form.hired_at')}</Typography>
        <TextField
          fullWidth
          type="date"
          value={userData.hired_at}
          onChange={(e) => setUserData({ ...userData, hired_at: e.target.value })}
        />
      </Row>

      <Row $order={26}>
        <Typography>{t('user_form.began_at')}</Typography>
        <TextField
          fullWidth
          type="date"
          value={userData.began_at}
          onChange={(e) => setUserData({ ...userData, began_at: e.target.value })}
        />
      </Row>

      <Row $order={27}>
        <Typography>{t('user_form.removed_at')}</Typography>
        <TextField
          fullWidth
          type="date"
          value={userData.removed_at}
          onChange={(e) => setUserData({ ...userData, removed_at: e.target.value })}
        />
      </Row>
      <Row $order={28}>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t('user_form.keep_history')}</FormLabel>
          <RadioGroup
            row
            value={userData.keep_data_history}
            onChange={(e) =>
              setUserData({ ...userData, keep_data_history: e.target.value === 'true' })
            }
          >
            <FormControlLabel value="true" control={<Radio />} label="Sim" />
            <FormControlLabel value="false" control={<Radio />} label="Não" />
          </RadioGroup>
        </FormControl>
      </Row>

      <Row $order={29}>
        <FormControl component="fieldset">
          <FormLabel component="legend">{t('user_form.show_in_reports')}</FormLabel>
          <RadioGroup
            row
            value={userData.show_in_reports}
            onChange={(e) =>
              setUserData({ ...userData, show_in_reports: e.target.value === 'true' })
            }
          >
            <FormControlLabel value="true" control={<Radio />} label="Sim" />
            <FormControlLabel value="false" control={<Radio />} label="Não" />
          </RadioGroup>
        </FormControl>
      </Row>
      <Row $order={30}>
        <Typography>{t('user_form.role_level')}</Typography>
        <Select
          fullWidth
          value={userData.role_level}
          onChange={(e) => setUserData({ ...userData, role_level: e.target.value })}
          displayEmpty
        >
          <MenuItem value="" disabled>{t('user_form.placeholder.role_level')}</MenuItem>
          <MenuItem value="administrador">Admin</MenuItem>
          <MenuItem value="supervisor">Supervisor</MenuItem>
          <MenuItem value="colaborador">Colaborador</MenuItem>
        </Select>
      </Row>
      <Row $order={31}>
        <Typography>{t('user_form.team')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.team')}
          value={userData.team}
          onChange={(e) => setUserData({ ...userData, team: e.target.value })}
        />
      </Row>
      <Row $order={32}>
        <Typography>{t('user_form.area')}</Typography>
        <TextField
          fullWidth
          placeholder={t('user_form.area')}
          value={userData.area}
          onChange={(e) => setUserData({ ...userData, area: e.target.value })}
        />
      </Row>
      <Row $order={33}>
        <Typography>{t('user_form.shift')}</Typography>
        <Select
          fullWidth
          value={userData.shift}
          onChange={(e) => setUserData({ ...userData, shift: e.target.value })}
          displayEmpty
        >
          <MenuItem value="" disabled>{t('user_form.placeholder.shift')}</MenuItem>
          {shiftOptions.map(option => (
            <MenuItem key={option.id} value={option.id}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </Row>
      <Row $order={2}>
        <Typography>{t('user_form.password')}</Typography>
        <TextField
          fullWidth
          type="password"
          placeholder={t('user_form.password')}
          value={userData.password}
          onChange={(e) => setUserData({ ...userData, password: e.target.value })}
        />
      </Row>
      <Row $order={21}>
        <Typography>{t('user_form.supervisor')}</Typography>
        <Select
          fullWidth
          multiple
          value={userData.supervisor}
          onChange={(e) => setUserData({ ...userData, supervisor: e.target.value })}
          renderValue={(selected) =>
            selected
              .map(id => {
                const match = userOptions.find(user => user.id === id)
                return match?.name || id
              })
              .join(', ')
          }
        >
          <MenuItem disabled value="">
            {t('user_form.placeholder.supervisor')}
          </MenuItem>
          {userOptions.map(user => (
            <MenuItem key={user.id} value={user.id}>
              {user.name}
            </MenuItem>
          ))}
        </Select>
      </Row>
      <Row $order={11}>
        <Typography>{t('user_form.usual_sleep')}</Typography>
        <Tooltip
          title={`${t('user_form.placeholder.usual_sleep')} (HH:MM-HH:MM)`}
          arrow
        >
          <TextField
            fullWidth
            placeholder={t('user_form.placeholder.usual_sleep')}
            value={userData.usual_sleep}
            onChange={(e) =>
              setUserData({ ...userData, usual_sleep: e.target.value })
            }
            inputProps={{
              pattern:
                '(?:[01]\\d|2[0-3]):[0-5]\\d-(?:[01]\\d|2[0-3]):[0-5]\\d',
            }}
          />
        </Tooltip>
      </Row>
      <Row $order={12}>
        <Typography>{t('user_form.caffeine')}</Typography>
        <Select
          fullWidth
          value={userData.caffeine?.split(',')[0] || ''}
          onChange={(e) =>
            setUserData({
              ...userData,
              caffeine: e.target.value,
            })
          }
          displayEmpty
        >
          <MenuItem value="" disabled>
            {t('user_form.placeholder.caffeine')}
          </MenuItem>
          {caffeineOptions.map(option => (
            <MenuItem key={option.id} value={option.name}>
              {option.name}
            </MenuItem>
          ))}
        </Select>
      </Row>
      <Row $order={13}>
        <Typography>{t('user_form.caffeine_start')}</Typography>
        <Tooltip
          title={`${t('user_form.placeholder.caffeine_start')} (HH:MM)`}
          arrow
        >
          <TextField
            fullWidth
            placeholder={t('user_form.placeholder.caffeine_start')}
            value={userData.caffeine_start}
            onChange={(e) =>
              setUserData({ ...userData, caffeine_start: e.target.value })
            }
            inputProps={{ pattern: '(?:[01]\\d|2[0-3]):[0-5]\\d' }}
          />
        </Tooltip>
      </Row>
    </FormContainer>
  )
}

export default UserForm
